import React, { useEffect, useMemo, useRef, useState } from "react";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CircleIcon from "@mui/icons-material/Circle";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, FormControlLabel, IconButton, Stack, Switch, Tooltip, Typography, useTheme } from "@mui/material";
import { groupBy } from "lodash";
import { DateTime } from "luxon";
import useAsyncRequest from "hooks/useAsyncRequest";
import { LoadablePage } from "shared/LoadablePage";
import type { EntityHistoryReferenceDto } from "client/api/EntityHistoryReferenceDto";
import { DateTimeText } from "components/DateTimeText/DateTimeText";
import type { Substantivity } from "components/EntityHistoryView/HistoryChangeDetailView";
import { classifyHistoryChange, HistoryChangeDetailView } from "components/EntityHistoryView/HistoryChangeDetailView";
import { useOctopusTheme, withTheme } from "components/Theme";

type EntityHistoryViewProps<T extends object> = {
  getHistoricalEntity: (asOf: DateTime) => Promise<T>;
  getEntityHistory: () => Promise<EntityHistoryReferenceDto[]>;
  projectFields?: (snapshot: T) => object;
};

type HistoryNodeType = "creation" | "update";
type HistoryNode = {
  key: string;
  type: HistoryNodeType;
  timestamp: DateTime;
  lastModifiedByUser: string;
  wasSystemUpdate: boolean;
};

type SnapshotEntry<T> = {
  entity?: T;
  error?: Error;
};

const SNAPSHOT_PREFETCH_CONCURRENCY = 6;

async function mapWithConcurrencyLimit<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  // One shared iterator hands each worker the next [index, item] pair, so every slot is filled exactly once.
  const entries = items.entries();
  const worker = async () => {
    for (const [i, item] of entries) {
      results[i] = await fn(item);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

const emailExpression: RegExp = /@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

function DashedLine() {
  const muiTheme = useTheme();
  const octopusTheme = useOctopusTheme();
  return (
    <div
      style={{
        borderLeftWidth: "1px",
        borderLeftStyle: "dashed",
        borderLeftColor: octopusTheme.primaryText,
        height: `calc(100% - ${muiTheme.spacing(3)})`,
        position: "absolute",
        left: muiTheme.spacing(1.5),
        top: muiTheme.spacing(3),
      }}
    />
  );
}

export function EntityHistoryView<T extends object>(props: EntityHistoryViewProps<T>) {
  const { getHistoricalEntity, getEntityHistory, projectFields } = props;
  const { data, loading, error } = useAsyncRequest(getEntityHistory);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [loadedRows, setLoadedRows] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [snapshots, setSnapshots] = useState<Map<string, SnapshotEntry<T>>>();
  const muiTheme = useTheme();

  const historyNodes: HistoryNode[] = useMemo(() => {
    const groupedDtos = groupBy(data, (dto) => dto.validFrom.toISO());
    const timestamps = Object.keys(groupedDtos).sort();

    const transformed: HistoryNode[] = timestamps.map((timestamp, i) => {
      //if this is the 1st item, it must be creation
      const nodeType: HistoryNodeType = i === 0 ? "creation" : "update";

      const lastModifiedByUser = groupedDtos[timestamp]?.[0]?.lastModifiedByUser || "Unknown";

      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return {
        key: timestamp,
        type: nodeType,
        timestamp: DateTime.fromISO(timestamp, { zone: "utc" }),
        lastModifiedByUser: lastModifiedByUser,
        wasSystemUpdate: !emailExpression.test(lastModifiedByUser),
      } as HistoryNode;
    });

    return transformed.reverse();
  }, [data]);

  const getHistoricalEntityRef = useRef(getHistoricalEntity);
  useEffect(() => {
    getHistoricalEntityRef.current = getHistoricalEntity;
  });

  useEffect(() => {
    if (historyNodes.length === 0) {
      setSnapshots(new Map());
      return;
    }

    let cancelled = false;
    setSnapshots(undefined);

    const fetchAll = async () => {
      const results = await mapWithConcurrencyLimit(
        historyNodes,
        SNAPSHOT_PREFETCH_CONCURRENCY,
        async (node): Promise<readonly [string, SnapshotEntry<T>]> => {
          try {
            const entity = await getHistoricalEntityRef.current(node.timestamp.plus({ milliseconds: 1 }));
            return [node.key, { entity }] as const;
          } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            return [node.key, { error }] as const;
          }
        }
      );
      if (cancelled) return;
      setSnapshots(new Map(results));
    };

    void fetchAll();
    return () => {
      cancelled = true;
    };
  }, [historyNodes]);

  const nodesWithSubstantivity = useMemo(() => {
    return historyNodes.map(
      (node, i): { node: HistoryNode; previousNode?: HistoryNode; substantivity: Substantivity } => {
        const previousNode = historyNodes[i + 1];
        if (node.type === "creation") return { node, previousNode, substantivity: "substantive" };
        if (!snapshots) return { node, previousNode, substantivity: "unknown" };

        const current = snapshots.get(node.key);
        const previous = previousNode ? snapshots.get(previousNode.key) : undefined;
        return {
          node,
          previousNode,
          substantivity: classifyHistoryChange(
            previous?.entity,
            current?.entity,
            previous?.error,
            current?.error,
            projectFields
          ),
        };
      }
    );
  }, [historyNodes, snapshots, projectFields]);

  const visibleNodes = useMemo(() => {
    return showAll
      ? nodesWithSubstantivity
      : nodesWithSubstantivity.filter(({ substantivity }) => substantivity !== "trivial");
  }, [nodesWithSubstantivity, showAll]);

  const hiddenCount = nodesWithSubstantivity.length - visibleNodes.length;
  const snapshotsReady = snapshots !== undefined;

  const getIcon = (type: HistoryNodeType) => {
    switch (type) {
      case "creation":
        return (
          <Tooltip title="Created" arrow>
            <AddCircleIcon />
          </Tooltip>
        );
      case "update":
        return (
          <Tooltip title="Updated" arrow>
            <EditIcon />
          </Tooltip>
        );
    }
  };

  const expandRow = (key: string) => {
    setExpandedRows((rows) => {
      const newRows = [...rows];
      if (newRows.indexOf(key) === -1) {
        newRows.push(key);
      }
      return newRows;
    });

    setLoadedRows((rows) => {
      const newRows = [...rows];
      if (newRows.indexOf(key) === -1) {
        newRows.push(key);
      }
      return newRows;
    });
  };

  const collapseRow = (key: string) => {
    setExpandedRows((rows) => {
      const idx = rows.indexOf(key);
      if (idx !== -1) {
        const newRows = [...rows];
        newRows.splice(idx, 1);
        return newRows;
      }
      return rows;
    });
  };

  return withTheme((theme) => (
    <LoadablePage loading={loading} error={error}>
      {(hiddenCount > 0 || showAll) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            pb: 1,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showAll}
                onChange={(_, checked) => setShowAll(checked)}
                slotProps={{
                  input: { "aria-label": "Show all history entries" },
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: theme.secondaryText }}>
                {showAll
                  ? "Show all history"
                  : `Show all history (${hiddenCount} ${hiddenCount === 1 ? "entry" : "entries"} hidden)`}
              </Typography>
            }
            labelPlacement="start"
          />
        </Box>
      )}
      <Stack spacing={0}>
        <Box
          sx={{
            position: "relative",
            pb: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            <Box
              sx={{
                width: muiTheme.spacing(3),
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Tooltip title="Current" arrow>
                <CircleIcon fontSize={"small"} />
              </Tooltip>
            </Box>
            <Typography
              sx={{
                color: theme.secondaryText,
                pl: 1,
              }}
            >
              Current
            </Typography>
          </Box>
          <DashedLine />
        </Box>
        {visibleNodes.map(({ node, previousNode: previousFullNode, substantivity }, i) => {
          const isExpanded = expandedRows.indexOf(node.key) !== -1;
          const isLoaded = loadedRows.indexOf(node.key) !== -1;
          const isLastItem = i === visibleNodes.length - 1;
          const olderVisibleNodeTimestamp = visibleNodes[i + 1]?.node.timestamp;
          const currentSnapshot = snapshots?.get(node.key);
          // Diff against the full-history previous node, not the next visible one, so hidden trivial entries don't collapse change-sets.
          const previousSnapshot = previousFullNode ? snapshots?.get(previousFullNode.key) : undefined;

          return (
            <Box
              key={node.key}
              sx={{
                position: "relative",
                pb: !isExpanded ? 4 : 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <Box
                  sx={{
                    width: muiTheme.spacing(3),
                  }}
                >
                  {getIcon(node.type)}
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    pl: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <DateTimeText dateTime={node.timestamp} labelFormat={"dd MMM yyyy, hh:mm:ss a"} />
                      <Typography
                        variant={"caption"}
                        component={"span"}
                        sx={{
                          color: theme.secondaryText,
                          px: 0.75,
                        }}
                      >
                        by
                      </Typography>
                      {node.wasSystemUpdate ? (
                        <Tooltip
                          title={node.lastModifiedByUser}
                          arrow
                          slotProps={{
                            tooltip: {
                              style: {
                                maxWidth: "none",
                              },
                            },
                          }}
                        >
                          <span style={{ color: theme.secondaryText }}>System</span>
                        </Tooltip>
                      ) : (
                        <span>{node.lastModifiedByUser}</span>
                      )}
                      {node.type !== "creation" && (
                        <Box
                          component={"span"}
                          sx={{
                            pl: 0.5,
                          }}
                        >
                          {!isExpanded ? (
                            <IconButton
                              size={"small"}
                              aria-label={"show-details"}
                              title={"Show details"}
                              onClick={() => expandRow(node.key)}
                            >
                              <ExpandMoreIcon />
                            </IconButton>
                          ) : (
                            <IconButton
                              size={"small"}
                              aria-label={"show-details"}
                              title={"Show details"}
                              onClick={() => collapseRow(node.key)}
                            >
                              <ExpandLessIcon />
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {/* We only show the details if it's expanded or loaded, is not the last item and has a previous value */}
                  {(isExpanded || isLoaded) && !isLastItem && !!olderVisibleNodeTimestamp && previousFullNode && (
                    <Box
                      sx={{
                        ml: -2.5,
                        my: 1,
                        backgroundColor: theme.ternaryBackground,

                        //if we have loaded the data, but it's not expanded, just hide this box, rather than unmount it
                        display: isLoaded && !isExpanded ? "none" : "block",
                      }}
                    >
                      <HistoryChangeDetailView
                        timestamp={node.timestamp}
                        previousTimestamp={previousFullNode.timestamp}
                        currentEntity={currentSnapshot?.entity}
                        currentEntityError={currentSnapshot?.error}
                        previousEntity={previousSnapshot?.entity}
                        previousEntityError={previousSnapshot?.error}
                        substantivity={substantivity}
                        projectFields={projectFields}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              {!isLastItem && <DashedLine />}
            </Box>
          );
        })}
        {!snapshotsReady && historyNodes.length > 0 && (
          <Box
            sx={{
              pt: 2,
              pl: 4,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.secondaryText,
                fontStyle: "italic",
              }}
            >
              Loading history details…
            </Typography>
          </Box>
        )}
      </Stack>
    </LoadablePage>
  ));
}
